import { alpha, type ThemeOptions } from '@mui/material/styles';

/** Dokploy's Azure visual language, expressed through AYRADE's MUI primitives. */
const applianceComponents = (
  mode: 'light' | 'dark',
  primary: string,
  paper: string,
): ThemeOptions['components'] => {
  const dark = mode === 'dark';
  const border = dark ? '#2b384a' : '#e1e8f0';
  const muted = dark ? '#a0aec1' : '#64748b';
  const surface = dark ? '#1f2a3b' : '#f0f5fa';
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          'borderRadius': 999,
          'textTransform': 'none',
          'fontWeight': 600,
          'fontSize': 13,
          'minHeight': 36,
          'padding': '8px 18px',
          'boxShadow': 'none',
          'transition': 'background-color 160ms, box-shadow 160ms',
          '&.Mui-focusVisible': {
            outline: `2px solid ${primary}`,
            outlineOffset: 3,
          },
          '&.icon-outlined': {
            minWidth: 36,
            padding: 8,
            borderColor: border,
          },
        },
        contained: { '&:hover': { boxShadow: `0 4px 16px ${alpha(primary, 0.2)}` } },
        outlined: { borderColor: border },
        sizeSmall: {
          minHeight: 30,
          padding: '4px 12px',
          fontSize: 12,
        },
        sizeLarge: {
          minHeight: 44,
          padding: '10px 24px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          'borderRadius': '50%',
          '&.Mui-focusVisible': {
            outline: `2px solid ${primary}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: paper,
          backgroundImage: 'none',
          borderRadius: 16,
        },
        elevation1: {
          border: `1px solid ${border}`,
          boxShadow: `0 4px 20px ${alpha('#00275f', dark ? 0.12 : 0.035)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${border}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '20px 24px 12px' },
        title: {
          fontSize: 16,
          fontWeight: 600,
        },
        subheader: {
          fontSize: 12,
          color: muted,
        },
      },
    },
    MuiCardContent: { styleOverrides: { root: { padding: 24 } } },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          border: `1px solid ${border}`,
          backgroundImage: 'none',
          boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 18,
          fontWeight: 600,
          padding: '24px 24px 16px',
          letterSpacing: '-0.025em',
        },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '16px 24px 24px' } } },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: `1px solid ${border}`,
          gap: 8,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42 },
        indicator: {
          height: 3,
          borderRadius: 3,
        },
        flexContainer: { gap: 4 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          'textTransform': 'none',
          'fontSize': 13,
          'fontWeight': 600,
          'minHeight': 42,
          'borderRadius': '10px 10px 0 0',
          '&.Mui-selected': { backgroundColor: alpha(primary, 0.07) },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          'borderRadius': 12,
          'backgroundColor': alpha(surface, 0.45),
          'fontSize': 13,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(primary, 0.12)}` },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          'fontSize': 13,
          '&::before': { borderBottomColor: border },
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: 13 } } },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
        },
        outlined: { borderColor: border },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          fontSize: 13,
          borderBottom: `1px solid ${border}`,
        },
        head: {
          color: muted,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          backgroundColor: surface,
        },
      },
    },
    MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: alpha(primary, 0.045) } } } },
    MuiTableContainer: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${border}`,
          borderRadius: 16,
          boxShadow: '0 12px 36px rgba(0,0,0,0.14)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          'borderRadius': 10,
          'margin': '3px 6px',
          'fontSize': 13,
          'minHeight': 36,
          '&.Mui-selected': {
            backgroundColor: alpha(primary, 0.1),
            boxShadow: 'none',
          },
          '&.Mui-selected:hover': { backgroundColor: alpha(primary, 0.16) },
        },
      },
    },
    MuiAccordion: {
      defaultProps: { slotProps: { transition: { unmountOnExit: true } } },
      styleOverrides: {
        root: {
          'border': `1px solid ${border}`,
          'borderRadius': 16,
          'boxShadow': 'none',
          '&::before': { display: 'none' },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: 13,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: 11,
          padding: '8px 12px',
        },
      },
    },
    MuiTypography: { styleOverrides: { root: { overflowWrap: 'break-word' } } },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: 12 },
        separator: { color: muted },
      },
    },
  };
};

export default applianceComponents;
